// The practice on the home board: load a line, rate every move the user plays, let the sparring
// partner answer, and wrap it up with a result. State is published to the `practice` store slice;
// practiceView.js renders it.

import { Chess } from "#vendor/chessjs/chess.js";
import { store, game } from "../state/index.ts";
import * as board from "./board.js";
import * as book from "./book.js";
import * as engine from "./engine.js";
import * as catalog from "../services/catalog.ts";
import * as api from "../services/api.ts";
import * as config from "./config.js";
import * as storage from "../services/storage.ts";
import * as progress from "./progress.js";
import * as track from "../services/track.ts";
import * as boardNav from "./boardNav.js";
import { buildUciHistory } from "./utils.js";
import { onPositionChanged } from "./positionOrchestrator.js";
import { lookup, sparringReplies, pickWeighted, hintMove, shareOf } from "../domain/catalogTree.ts";
import {
    afterUserMove, reachedMoveLimit, usesCatalogBook, liveReplies, countsAsPractice, isWallReached, endTitle, END_REASON, DEFAULT_MAX_OWN_MOVES,
} from "../domain/practiceRules.ts";
import {
    rateFromCatalog, scoreToCp, winLoss, classify, moveAccuracy, sessionAccuracy, isSolid,
    moveStatsFromExplorer, moveStatsFromMyGames, selectInsight,
} from "../domain/moveRating.ts";
import { xpForMove, sessionBonuses, starsFor, bonusContext, rewardsOf } from "../domain/progressRules.ts";
import { snapshotOf, restoreSession, isUsable, resumable } from "../domain/practiceSnapshot.ts";

const SPARRING_DELAY_MS = [550, 950];
const VERDICT_WAIT_MS = 1500;          // the sparring partner does not wait longer than this for a verdict
const RESULT_VERDICT_WAIT_MS = 4000;   // the results do, a bit: the last move deserves its rating
const FIRST_MOVE_DELAY_MS = 800;
const RARE_MOVE_SHARE = 0.04;
const HOOK_EVERY_MOVES = 4;

let tree = null;
let session = null;
let turnToken = 0;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const between = ([min, max]) => min + Math.random() * (max - min);

function uuid() {
    return crypto.randomUUID ? crypto.randomUUID()
        : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });
}

/** SAN of a UCI move in a position, or null when the move is not legal there. Never throws (chess.js does). */
function uciToSan(fen, uci) {
    if (!uci) return null;
    const legal = new Chess(fen).moves({ verbose: true })
        .find((m) => m.from === uci.slice(0, 2) && m.to === uci.slice(2, 4) && (!m.promotion || m.promotion === (uci[4] || "q")));
    return legal ? legal.san : null;
}

/** "6...Bg7" / "6.Nf3", numbered from the position the move was played in. */
function moveLabel(fenBefore, san) {
    const parts = fenBefore.split(" ");
    return parts[1] === "w" ? `${parts[5]}.${san}` : `${parts[5]}...${san}`;
}

function isLoggedIn() {
    return !!store.get('user.currentUser');
}

/** A catalog line answers from its own tree only without the live explorer (practiceRules.usesCatalogBook). */
function catalogBook(line) {
    return usesCatalogBook({
        source: line.source, loggedIn: isLoggedIn(), lichessConnected: !!store.get('user.lichessConnected'),
    });
}

export function getTree() {
    return tree;
}

export function isActive() {
    return ["ready", "playing", "offbook"].includes(store.get('practice.status'));
}

// ---------------------------------------------------------------------------------------------
// Starting a practice
// ---------------------------------------------------------------------------------------------

/**
 * What the board page opens with: the line asked for in the URL, the one left pending before logging
 * in, or nothing at all.
 *
 * Where it opens decides one thing. On the study's own page, a practice left in the middle is put
 * back as it was: that is the page it belongs to. Anywhere else — the front page, an opening page —
 * it is only offered, through `practice.resumable` and a button, because landing somewhere has to
 * give you what you asked for and not whatever this tab was doing twenty minutes ago.
 *
 * Answers "practice" when a line is on the board, "dashboard" when there is an account to show its
 * own home to instead of a line it did not ask for, and "empty" when the catalog cannot be read.
 *
 * @param options.tool  the tool this page is, when it is one ("study")
 */
export async function bootstrap({ tool = null } = {}) {
    await progress.load();
    const waiting = resumable(storage.loadPracticeSnapshot());
    store.set('practice.resumable', waiting);
    if (!waiting) storage.clearPracticeSnapshot();
    try {
        // An opening page (/openings/<slug>) names its line in the markup; ?line= does the same for a link
        const params = new URLSearchParams(window.location.search);

        // Coming back from /lines with one of the account's own lines chosen.
        const userLineId = params.get("userLine");
        if (userLineId) {
            if (await resume({ userLineId })) {
                window.history.replaceState({}, "", window.location.pathname);
                return "practice";
            }
            // Every line, the ones studied once included: they are practised like any other, only never listed
            const own = await api.fetchLines(true);
            const line = own.find((candidate) => String(candidate.id) === userLineId);
            if (line) {
                await startLine({
                    source: "user", id: line.id, name: line.name, color: line.color || "white",
                    fen: line.fen, uci: line.uciMoves,
                });
                window.history.replaceState({}, "", window.location.pathname);
                return "practice";
            }
        }

        // The line somebody was playing when the login wall stopped them. It is only taken when
        // nothing else was asked for, and it belongs to the study: that is where they were.
        const asked = document.body.dataset.line || params.get("line");
        const pending = asked ? null : storage.takeResume();
        if (pending && tool !== "study") {
            window.location.replace(`/tools/study?line=${encodeURIComponent(pending)}`);
            return "practice";
        }
        const wanted = asked || pending;
        // The same line that was left in the middle: it comes back with everything played on it
        if (wanted && await resume({ slug: wanted })) return "practice";

        const wantedLine = wanted ? await catalog.findLine(wanted) : null;
        if (wantedLine) {
            await startLine({ ...wantedLine, source: "catalog" });
            return "practice";
        }
        // Nothing was asked for. On the study's own page, that means carrying on where they were —
        // and the study is only served without a line when there is one waiting.
        if (tool === "study" && await resume()) return "practice";

        const line = await pickLine();
        if (!line) return "empty";
        // A line the page deals by itself is not one they chose, so it is not kept as theirs
        await startLine({ ...line, source: "catalog" }, { auto: tool !== "study" });
        return "practice";
    } catch (err) {
        console.warn("[practice] Could not load the line catalog", err);
        store.set('practice.status', "unavailable");
        return "empty";
    }
}

function pickLine() {
    return catalog.pickRandomLine(storage.loadRecentLines(), { newcomer: progress.completedCount() === 0 });
}

export async function startAnotherLine() {
    const line = await pickLine();
    if (line) await startLine({ ...line, source: "catalog" });
}

export function restart() {
    const line = store.get('practice.line');
    if (line) return startLine(line);
    return Promise.resolve();
}

/**
 * @param line  { source: "catalog" | "user", slug | id, name, color, fen?, uci, idea?, moves? }
 * @param options.retryFromPly  replay the previous practice up to that ply and continue from there;
 *                              a retry is for learning, it is never recorded
 * @param options.auto          the home page dealt this line by itself. Nobody asked for it, so it is
 *                              not kept as the practice to come back to: that belongs to the line the
 *                              user did choose, which may well be waiting in this same tab.
 */
export async function startLine(line, { retryFromPly = null, history = null, auto = false } = {}) {
    // A line of the user has no pre-generated book: its sparring moves come live from Lichess
    if (line.source === "user" && !store.get('user.lichessConnected')) {
        store.set('practice.notice', "Your own lines get their sparring moves live from Lichess. Connect your Lichess account from the user menu (it is free) to practice them.");
        return;
    }
    store.set('practice.notice', null);
    const token = ++turnToken;
    const locked = retryFromPly === null && isWallReached({
        isLoggedIn: isLoggedIn(), completedCount: progress.completedCount(), limit: store.get('progress.anonymousLimit'),
    });

    // The practice on the board keeps its tree until the new line is really taking over
    let nextTree = null;
    if (catalogBook(line)) {
        try {
            nextTree = await catalog.loadTree(line.slug);
        } catch (err) {
            console.warn("[practice] No position tree for", line.slug, err);
        }
        if (token !== turnToken) return;
    }
    tree = nextTree;
    book.setTreeProvider(() => tree);

    session = {
        id: uuid(), line, startedAt: new Date().toISOString(), startingFen: null, moves: [], attempts: [],
        hintedPly: null, hooksShown: 0, takebacks: 0, hints: 0, isRetry: retryFromPly !== null,
        maxOwnMoves: line.maxOwnMoves || DEFAULT_MAX_OWN_MOVES, auto,
    };

    // Board: the user's side at the bottom, and only that side can be moved
    store.set('board.userSide', line.color);
    const boardSideSelect = document.getElementById("boardSide");
    if (boardSideSelect) boardSideSelect.value = line.color;
    config.updateBoardOrientation();
    store.batch((set) => {
        set('studyLine.baseFen', line.fen || null);
        set('studyLine.uciMoves', line.uci || null);
        set('studyLine.name', line.name);
    });
    board.loadLine(line.fen || null, line.uci || "");
    if (retryFromPly !== null && history) {
        history.slice(game.history().length, retryFromPly).forEach((san) => game.move(san));
        board.syncBoard();
    }

    session.startingFen = game.fen();

    store.batch((set) => {
        set('book.opening', null);
        set('practice.line', line);
        set('practice.hasTree', !!tree);
        set('practice.status', locked ? "locked" : "ready");
        set('practice.moves', []);
        set('practice.ownMoves', 0);
        set('practice.combo', 0);
        set('practice.bestCombo', 0);
        set('practice.xp', 0);
        set('practice.accuracy', null);
        set('practice.offbook', null);
        set('practice.hintUci', null);
        set('practice.result', null);
        set('practice.isRetry', session.isRetry);
        set('practice.revision', store.get('practice.revision') + 1);
    });
    board.setMovableSide(locked ? "none" : movableSide());
    board.updateBookOptions();
    boardNav.resetView();   // the arrows must not replay moves of the previous practice
    requestAnimationFrame(() => board.redrawBoard());
    onPositionChanged();
    remember();   // the line alone is already worth coming back to

    if (locked) {
        track.event("wall_shown", { line: line.slug });
        return;
    }
    if (line.slug) storage.pushRecentLine(line.slug);
    track.event("practice_loaded", { line: line.slug || "user-line" });

    // Off the critical path: the engine is only needed for moves the catalog has no evaluation for.
    setTimeout(() => engine.warmUp(), 1500);

    if (game.turn() !== line.color[0]) {
        await delay(FIRST_MOVE_DELAY_MS);
        if (token === turnToken) playSparringMove(game.fen(), token);
    } else {
        prefetchIfNeeded();
    }
}

/**
 * Puts the practice this tab was in the middle of back on the board: the same line, the same moves,
 * the same verdicts, the same turn. Returns false when there is nothing to resume, or when what was
 * stored no longer replays, and the caller then starts a line the usual way.
 */
export async function resume({ slug = null, userLineId = null } = {}) {
    const snapshot = storage.loadPracticeSnapshot();
    if (!isUsable(snapshot)) {
        storage.clearPracticeSnapshot();
        store.set('practice.resumable', null);
        return false;
    }
    const line = snapshot.line;
    // Asked for a different line (a link, an opening page): that wins over what was left here
    if (slug && line.slug !== slug) return false;
    if (userLineId && !(line.source === "user" && String(line.id) === String(userLineId))) return false;
    // A line of the user's needs Lichess for its replies, exactly as it did when it started
    if (line.source === "user" && !store.get('user.lichessConnected')) return false;

    const token = ++turnToken;
    let nextTree = null;
    if (catalogBook(line)) {
        try {
            nextTree = await catalog.loadTree(line.slug);
        } catch (err) {
            console.warn("[practice] No position tree for", line.slug, err);
        }
        if (token !== turnToken) return false;
    }

    // The board first: the line, then every move that was played on top of it
    store.set('board.userSide', line.color);
    const boardSideSelect = document.getElementById("boardSide");
    if (boardSideSelect) boardSideSelect.value = line.color;
    config.updateBoardOrientation();
    store.batch((set) => {
        set('studyLine.baseFen', line.fen || null);
        set('studyLine.uciMoves', line.uci || null);
        set('studyLine.name', line.name);
    });
    board.loadLine(line.fen || null, line.uci || "");
    if (!replay(snapshot.history)) {
        // The stored moves do not fit the line any more: start it over rather than show half a game
        storage.clearPracticeSnapshot();
        board.loadLine(line.fen || null, line.uci || "");
        return false;
    }

    tree = nextTree;
    book.setTreeProvider(() => tree);
    const restored = restoreSession(snapshot);
    session = { ...restored.session, line };

    board.syncBoard();

    store.batch((set) => {
        set('book.opening', null);
        set('practice.notice', null);
        set('practice.line', line);
        set('practice.hasTree', !!tree);
        set('practice.status', restored.status);
        set('practice.offbook', restored.offbook);
        set('practice.hintUci', null);
        set('practice.result', null);
        set('practice.isRetry', session.isRetry);
        set('practice.revision', store.get('practice.revision') + 1);
    });
    recomputeTotals();
    publishMoves();   // which also clears the offer: this practice is the one on the board now
    board.setMovableSide(restored.status === "offbook" ? "none" : movableSide());
    board.updateBookOptions();
    boardNav.resetView();
    boardNav.onMoveMade();   // the arrows start from the position that is really on the board
    requestAnimationFrame(() => board.redrawBoard());
    onPositionChanged();
    setTimeout(() => engine.warmUp(), 1500);
    track.event("practice_resumed", { line: line.slug || "user-line", moves: session.attempts.length });

    // A verdict that was still being computed when the page was left never arrived: ask again
    session.moves.filter((m) => m && m.actor === "user" && m.verdict.status === "pending").forEach(rerate);

    if (restored.status !== "offbook" && game.turn() !== line.color[0]) {
        await delay(between(SPARRING_DELAY_MS));
        if (token === turnToken) playSparringMove(game.fen(), token);
    } else {
        prefetchIfNeeded();
    }
    return true;
}

/** Plays the moves the practice had on the board. False if any of them no longer fits the position. */
function replay(history) {
    for (const san of history.slice(game.history().length)) {
        // chess.js throws on a move it does not know: look it up instead of trying it
        const legal = game.moves().includes(san);
        if (!legal) {
            console.warn("[practice] The practice kept for this tab no longer replays at", san);
            return false;
        }
        game.move(san);
    }
    return game.history().length === history.length;
}

/** Rates a move again, with whatever the position can tell us now. */
function rerate(record) {
    record.settled = rateUserMove(record, captureContext(record.fenBefore))
        .catch((err) => {
            console.warn("[practice] Could not rate the move", err);
            record.verdict = { status: "unavailable" };
            record.insight = record.insight || selectInsight({ verdict: null });
        })
        .then(() => {
            if (session && session.attempts.includes(record)) {
                recomputeTotals();
                publishMoves();
            }
        });
}

/** Continues from the position before `ply` of the practice that just ended. */
export function retryFrom(ply) {
    const line = store.get('practice.line');
    const history = store.get('practice.result')?.history;
    if (!line || !history) return Promise.resolve();
    track.event("retry", { line: line.slug });
    return startLine(line, { retryFromPly: ply, history });
}

// ---------------------------------------------------------------------------------------------
// The turn loop
// ---------------------------------------------------------------------------------------------

/** Who may move pieces: only the user's side, or both when the sparring moves are made by hand. */
function movableSide() {
    return config.isAutoPlayEnabled() ? session.line.color : null;
}

/** The "sparring moves" option changed while a practice is on the board. */
export function onAutoPlayChanged() {
    if (!session || !isActive()) return;
    // Off the book the board waits for "Take back" or "Finish here": there is no reply to play
    if (store.get('practice.status') === "offbook") return;
    board.setMovableSide(movableSide());
    if (config.isAutoPlayEnabled() && game.turn() !== session.line.color[0]) {
        playSparringMove(game.fen(), ++turnToken);
    }
}

/** Wired to the board: a move has just been played on it. */
export function onUserMove({ san, uci, fenBefore, fenAfter }) {
    if (!session || !isActive()) return false;
    // With manual sparring moves the user plays the other side by hand: that is a sparring move, not theirs
    if (fenBefore.split(" ")[1] !== session.line.color[0]) {
        turnToken++;
        onSparringMove({ fenBefore, san, uci });
        return true;
    }
    const token = ++turnToken;
    const ply = game.history().length - 1;

    // Everything the verdict's context needs belongs to the position BEFORE the move, and the
    // store is about to move on to the next one: capture it now.
    const context = captureContext(fenBefore);
    // The moves that led to the position, from where the line started: what the repertoire is asked about
    context.path = buildUciHistory().slice(0, ply);

    session.moves = session.moves.slice(0, ply);   // a move played after navigating back starts a new branch
    const record = {
        ply, actor: "user", san, uci, fenBefore, label: moveLabel(fenBefore, san),
        verdict: { status: "pending" }, insight: null, stats: null, xp: 0, combo: 0,
        hinted: session.hintedPly === ply,
    };
    session.moves[ply] = record;
    session.attempts.push(record);
    session.hintedPly = null;
    if (session.attempts.length === 1) track.event("practice_started", { line: session.line.slug || "user-line" });

    store.batch((set) => {
        set('practice.status', "playing");
        set('practice.offbook', null);
        set('practice.hintUci', null);
    });
    board.updateBookOptions();
    publishMoves();

    // Whatever goes wrong while rating, the turn goes on: the move simply stays without a verdict
    record.settled = rateUserMove(record, context)
        .catch((err) => {
            console.warn("[practice] Could not rate the move", err);
            record.verdict = { status: "unavailable" };
            record.insight = record.insight || selectInsight({ verdict: null });
        })
        .then(() => {
            if (session && session.attempts.includes(record)) {
                recomputeTotals();
                publishMoves();
            }
        });
    decideNext({ record, fenBefore, fenAfter, context, token });
    return true;
}

function captureContext(fenBefore) {
    const node = lookup(tree, fenBefore);
    const liveBook = store.get('book.fen') === fenBefore ? store.get('book.latestMoves') : null;
    const myGames = store.get('myGames.fen') === fenBefore ? store.get('myGames.latestMoves') : null;
    // Like the book and the user's games, the repertoire answer only counts if it is about this position
    const repertoireFresh = store.get('repertoire.fen') === fenBefore;
    const repertoireMoves = repertoireFresh ? store.get('repertoire.latestMoves') || [] : [];
    const repertoireStatus = repertoireFresh ? store.get('repertoire.status') : null;
    return {
        node,
        bookMoves: node ? node.m.map((m) => ({ uci: m[0], san: m[1], white: m[2], draws: m[3], black: m[4] })) : liveBook,
        myGames,
        repertoire: {
            // Whether the question was answered for this position at all: without an answer the
            // verdict says nothing about the repertoire rather than guessing.
            known: !!repertoireStatus,
            hasRepertoire: !!repertoireStatus && !repertoireStatus.noRepertoire,
            covers: !!repertoireStatus && !!repertoireStatus.inRepertoire,
            available: !!repertoireStatus && !repertoireStatus.noRepertoire && repertoireMoves.length > 0,
            expectedUci: repertoireMoves.map((pv) => pv.split(/\s+/)[0]),
        },
    };
}

async function rateUserMove(record, context) {
    const { fenBefore, uci } = record;
    let rated = rateFromCatalog(context.node, uci);
    let source = "catalog";
    if (!rated) {
        source = "engine";
        const result = await engine.rateMove(fenBefore, uci);
        if (result) {
            const bestCp = scoreToCp(result.best);
            const loss = winLoss(bestCp, scoreToCp(result.played));
            rated = {
                verdict: classify(loss, { sameAsBest: result.sameAsBest }), loss,
                bestUci: result.bestUci, bestSan: uciToSan(fenBefore, result.bestUci),
                bestCp, playedCp: scoreToCp(result.played),
            };
        }
    }

    const moverColor = fenBefore.split(" ")[1] === "w" ? "white" : "black";
    const db = context.bookMoves ? moveStatsFromExplorer(context.bookMoves, uci, moverColor) : null;
    const mine = context.myGames ? moveStatsFromMyGames(context.myGames, uci) : null;
    const rep = context.repertoire;
    const expectedSan = rep.expectedUci.map((move) => uciToSan(fenBefore, move)).filter(Boolean);
    const inRepertoire = rep.expectedUci.includes(uci);
    const repertoire = rep.known
        ? { hasRepertoire: rep.hasRepertoire, covers: rep.covers, inRepertoire, expectedSan }
        : null;
    record.stats = { db, mine, repertoire };
    keepRepertoireAttempt(record, context);
    record.verdict = rated
        ? { status: "rated", code: rated.verdict, loss: rated.loss, accuracy: moveAccuracy(rated.loss), bestSan: rated.bestSan, bestUci: rated.bestUci, bestCp: rated.bestCp ?? null, playedCp: rated.playedCp ?? null, source }
        : { status: "unavailable" };

    // A visitor is shown, now and then, what their own games would add to this sentence.
    const hookDue = !isLoggedIn() && session.attempts.length % HOOK_EVERY_MOVES === 0 && session.hooksShown < 2;
    record.insight = selectInsight({
        verdict: rated ? rated.verdict : null,
        bestSan: rated && rated.bestUci !== uci ? rated.bestSan : null,
        db, mine,
        repertoire: { available: rep.available, inRepertoire, expectedSan },
        hooks: { games: hookDue },
    });
    if (record.insight.code === "hook_games") session.hooksShown++;
}

/**
 * Every move played where the user has a repertoire is kept, taken back or not and whether the practice ends or is
 * left (docs/REPERTOIRES.md): a move that is not the repertoire's is the user forgetting it. The server checks it
 * against the repertoire and keeps only the ones it has an answer for; a user known to have none is not asked.
 */
function keepRepertoireAttempt(record, context) {
    const rep = context.repertoire;
    if (!isLoggedIn() || (rep.known && !rep.hasRepertoire)) return;
    api.recordRepertoireAttempt({
        sessionId: session.id,
        attemptIndex: session.attempts.indexOf(record),
        startFen: session.line.fen || null,
        path: context.path,
        uci: record.uci,
        hinted: !!record.hinted,
    }).catch((err) => console.warn("[practice] Could not keep the repertoire attempt", err));
}

/** Combo and XP follow the moves currently on the board; accuracy counts every attempt, take-backs included. */
function recomputeTotals() {
    let combo = 0;
    let bestCombo = 0;
    let xp = 0;
    session.moves.filter((m) => m && m.actor === "user").forEach((m) => {
        if (m.verdict.status !== "rated") return;
        combo = isSolid(m.verdict.code) && !m.hinted ? combo + 1 : 0;
        bestCombo = Math.max(bestCombo, combo);
        m.combo = combo;
        m.xp = session.isRetry ? 0 : xpForMove(m.verdict.code, combo, { hinted: m.hinted });
        xp += m.xp;
    });
    session.combo = combo;
    session.bestCombo = Math.max(session.bestCombo || 0, bestCombo);
    session.xp = xp;
    session.accuracy = sessionAccuracy(ratedLosses());
}

function ratedLosses() {
    return session.attempts.filter((m) => m.verdict.status === "rated" && !m.hinted).map((m) => m.verdict.loss);
}

/**
 * Keeps the practice on this tab. Every page of the site is a page of its own, so walking over to
 * /settings unloads the board: without this, the moves played were gone on the way back.
 */
function remember() {
    // A line the home page dealt on its own is not a practice anybody started, so it neither becomes
    // the one to come back to nor touches the one already waiting there.
    if (!session || session.auto) return;
    const snapshot = snapshotOf({
        session,
        status: store.get('practice.status'),
        history: boardNav.playedHistory(),
        offbook: store.get('practice.offbook'),
    });
    if (snapshot) {
        storage.savePracticeSnapshot(snapshot);
    } else {
        storage.clearPracticeSnapshot();
    }
    // What is on the board is now what is stored: there is nothing else to come back to
    store.set('practice.resumable', null);
}

function publishMoves() {
    const ownMoves = session.moves.filter((m) => m && m.actor === "user").length;
    store.batch((set) => {
        set('practice.moves', session.moves.map((m) => (m ? { ...m, settled: undefined } : m)));
        set('practice.ownMoves', ownMoves);
        set('practice.combo', session.combo || 0);
        set('practice.bestCombo', session.bestCombo || 0);
        set('practice.xp', session.xp || 0);
        set('practice.accuracy', session.accuracy ?? null);
        set('practice.revision', store.get('practice.revision') + 1);
    });
    remember();
}

async function decideNext({ record, fenBefore, fenAfter, context, token }) {
    const ownMoves = session.moves.filter((m) => m && m.actor === "user").length;
    // The repertoire's own move keeps the practice going past its usual length (practiceRules.reachedMoveLimit)
    const repertoireMove = context.repertoire.available && context.repertoire.covers
        && context.repertoire.expectedUci.includes(record.uci);
    let decision;
    if (tree) {
        decision = afterUserMove({
            tree, fenBefore, fenAfter, uci: record.uci, ownMoves,
            maxOwnMoves: session.maxOwnMoves, gameOver: game.isGameOver(), repertoireMove,
        });
    } else {
        decision = await decideWithLiveBook({ record, fenAfter, context, ownMoves, repertoireMove });
        if (token !== turnToken) return;
    }

    // The book ending on a move that is not the repertoire's is not the line finished: it is a wrong
    // turn, and it can be taken back like any move that leaves the book
    const rep = context.repertoire;
    if (decision.next === "end" && decision.reason === END_REASON.LINE_COMPLETE && rep.available
        && rep.covers && !rep.expectedUci.includes(record.uci)) {
        decision = { next: "offbook", rarity: "repertoire" };
    }

    if (decision.next === "reply") {
        await Promise.all([delay(between(SPARRING_DELAY_MS)), Promise.race([record.settled, delay(VERDICT_WAIT_MS)])]);
        if (token === turnToken) playSparringMove(fenAfter, token);
    } else if (decision.next === "end") {
        await Promise.race([record.settled, delay(RESULT_VERDICT_WAIT_MS)]);
        if (token === turnToken) finish(decision.reason);
    } else if (decision.next === "pause") {
        store.set('practice.notice', "Lichess is not answering right now. Take the move back and try again in a moment, or finish here.");
        board.setMovableSide("none");
        store.batch((set) => {
            set('practice.status', "offbook");
            set('practice.offbook', { rarity: "paused", ply: record.ply });
        });
        remember();
    } else {
        board.setMovableSide("none");
        store.batch((set) => {
            set('practice.status', "offbook");
            set('practice.offbook', { rarity: decision.rarity, ply: record.ply });
        });
        remember();
    }
}

/** Same rules as the catalog tree, asked to the live explorer. */
async function decideWithLiveBook({ record, fenAfter, context, ownMoves, repertoireMove }) {
    if (game.isGameOver()) return { next: "end", reason: END_REASON.GAME_OVER };
    if (reachedMoveLimit({ ownMoves, maxOwnMoves: session.maxOwnMoves, repertoireMove })) {
        return { next: "end", reason: END_REASON.MAX_MOVES };
    }
    const after = await book.fetchBook(fenAfter);
    // Lichess not answering (rate limit, network) is not the end of the book: finishing here would record a
    // practice as completed that was only interrupted
    if (after.error) return { next: "pause" };
    const replies = liveReplies(after.moves);
    if (replies.length) return { next: "reply" };
    if (repertoireMove) return { next: "reply" };

    const stats = context.bookMoves ? moveStatsFromExplorer(context.bookMoves, record.uci, "white") : null;
    const known = stats && stats.n > 0;
    if (known && stats.sharePct / 100 >= RARE_MOVE_SHARE) return { next: "end", reason: END_REASON.LINE_COMPLETE };
    return { next: "offbook", rarity: known ? "rare" : "unknown" };
}

async function playSparringMove(fen, token) {
    if (token !== turnToken || game.fen() !== fen || !isActive()) return;
    if (!config.isAutoPlayEnabled()) return;   // manual mode: the user picks the reply from the sparring list

    let replies = sparringReplies(lookup(tree, fen));
    // Past the end of the line's own tree (only the repertoire takes it there) or without one: the live explorer,
    // and when it has nothing either, the moves the repertoire has for the partner here
    if (!replies.length) {
        const live = await book.fetchBook(fen);
        if (token !== turnToken || game.fen() !== fen) return;
        replies = liveReplies(live.moves || []);
        if (!replies.length) replies = repertoireReplies(fen);
    }
    const move = pickWeighted(replies);
    if (!move) {
        finish(END_REASON.LINE_COMPLETE);
        return;
    }
    book.applyBookMove(move);
}

/** The repertoire's moves for whoever is to move in `fen`, as book replies of equal weight. */
function repertoireReplies(fen) {
    if (store.get('repertoire.fen') !== fen) return [];
    const chess = new Chess(fen);
    return (store.get('repertoire.latestMoves') || []).map((pv) => {
        const uci = pv.split(/\s+/)[0];
        const move = chess.moves({ verbose: true }).find((m) => m.from + m.to + (m.promotion || "") === uci);
        return move ? { uci, san: move.san, white: 1, draws: 0, black: 0 } : null;
    }).filter(Boolean);
}

/** Wired to book.js: a sparring move was applied, by autoplay or picked by hand in manual mode. */
export function onSparringMove({ fenBefore, san, uci }) {
    if (!session || !isActive()) return;
    const ply = game.history().length - 1;
    const node = lookup(tree, fenBefore);
    session.moves = session.moves.slice(0, ply);
    session.moves[ply] = {
        ply, actor: "rival", san, uci, fenBefore, label: moveLabel(fenBefore, san),
        sharePct: node ? Math.round(shareOf(node, uci) * 100) : null,
    };
    if (store.get('practice.status') === "ready") store.set('practice.status', "playing");
    publishMoves();
    // Every source now describes the position the user has to play
    onPositionChanged();
    prefetchIfNeeded();
}

/** Outside the catalog the verdict needs the engine: let it think while the user does. */
function prefetchIfNeeded() {
    const fen = game.fen();
    if (!lookup(tree, fen)) engine.prefetchPosition(fen);
}

// ---------------------------------------------------------------------------------------------
// Help
// ---------------------------------------------------------------------------------------------

/** Takes the last own move back (and the sparring reply to it, if there was one). */
export function takeBack() {
    if (!session) return;
    const last = [...session.moves].reverse().find((m) => m && m.actor === "user");
    if (!last) return;
    turnToken++;
    session.takebacks++;
    // Looking at an earlier position, the game no longer holds the later moves: return to the end first,
    // or the undo would be computed on a truncated history and leave board and practice out of step
    if (boardNav.isViewingPast()) boardNav.goToEnd();
    session.moves = session.moves.slice(0, last.ply);
    board.jumpToPly(last.ply - 1);
    board.setMovableSide(movableSide());
    boardNav.onMoveMade();   // the moves taken back are gone from the navigation too
    recomputeTotals();
    store.batch((set) => {
        set('practice.status', session.moves.some((m) => m && m.actor === "user") ? "playing" : "ready");
        set('practice.offbook', null);
        set('practice.hintUci', null);
    });
    publishMoves();
    track.event("take_back", { line: session.line.slug });
}

/** Marks the piece to move. The hinted move earns no XP and stays out of the accuracy. */
export function hint() {
    if (!session || !isActive() || game.turn() !== session.line.color[0]) return null;
    const fen = game.fen();
    const suggestion = hintMove(lookup(tree, fen)) || (store.get('book.fen') === fen ? store.get('book.latestMoves')[0] : null);
    if (!suggestion) return null;
    session.hintedPly = game.history().length;
    session.hints++;
    store.set('practice.hintUci', suggestion.uci);
    board.updateBookOptions();
    return suggestion;
}

// ---------------------------------------------------------------------------------------------
// Ending
// ---------------------------------------------------------------------------------------------

/** The user decides to stop where the book was left. */
export function finishOffBook() {
    if (store.get('practice.status') === "offbook") finish(END_REASON.OFF_BOOK);
}

export function finishNow() {
    if (isActive()) finish(END_REASON.MAX_MOVES);
}

function finish(reason) {
    if (!session) return;
    turnToken++;
    recomputeTotals();
    board.setMovableSide("none");

    const userMoves = session.moves.filter((m) => m && m.actor === "user");
    const rated = session.attempts.filter((m) => m.verdict.status === "rated" && !m.hinted);
    const counts = { best: 0, good: 0, inaccuracy: 0, mistake: 0, blunder: 0 };
    rated.forEach((m) => { counts[m.verdict.code]++; });
    const slip = rated.reduce((worst, m) => (m.verdict.loss > (worst ? worst.verdict.loss : 3) ? m : worst), null);
    const accuracy = session.accuracy ?? null;
    const counted = !session.isRetry && countsAsPractice({ ownMoves: userMoves.length, endReason: reason });

    const before = progress.getSummary();
    const bonuses = counted
        ? sessionBonuses({ accuracy, ratedMoves: rated.length, ...bonusContext(before, { slug: lineKey(), accuracy }) })
        : [];
    const moveXp = session.xp || 0;
    const xp = counted ? moveXp + bonuses.reduce((sum, b) => sum + b.xp, 0) : 0;
    const previousBest = before.lines[lineKey()]?.bestAccuracy ?? null;

    const entry = {
        id: session.id, tool: "study",
        slug: lineKey(), catalogSlug: session.line.source === "catalog" ? session.line.slug : null,
        userLineId: session.line.source === "user" ? session.line.id : null,
        lineName: session.line.name, color: session.line.color, startingFen: session.startingFen,
        startedAt: session.startedAt, endedAt: new Date().toISOString(),
        endReason: reason, ownMoves: userMoves.length, ratedMoves: rated.length, accuracy, depth: userMoves.length,
        bestCombo: session.bestCombo || 0, counts, xp, counted, hints: session.hints, takebacks: session.takebacks,
    };
    const { after } = session.isRetry ? { after: before } : progress.record(entry, movesForServer());

    store.batch((set) => {
        set('practice.status', "finished");
        set('practice.offbook', null);
        set('practice.hintUci', null);
        set('practice.result', {
            reason, title: endTitle(reason), counted, isRetry: session.isRetry, accuracy, ratedMoves: rated.length,
            stars: starsFor(accuracy, rated.length), ownMoves: userMoves.length, counts, bestCombo: session.bestCombo || 0,
            moveXp, bonuses, xp, previousBest, isPersonalBest: previousBest !== null && accuracy !== null && accuracy > previousBest,
            rewards: rewardsOf(before, after), practiceNumber: after.completedCount,
            slip: slip ? { ply: slip.ply, label: slip.label, bestSan: slip.verdict.bestSan, verdict: slip.verdict.code } : null,
            history: game.history(),
        });
    });
    board.updateBookOptions();
    if (!session.auto) {
        storage.clearPracticeSnapshot();   // it is over: there is nothing to come back to
        store.set('practice.resumable', null);
    }
    if (!session.isRetry) {
        track.event("practice_completed", { line: session.line.slug || "user-line", practice: after.completedCount, counted, reason });
    }
}

/** Stable key of the line in the practice log: the catalog slug, or "line-<id>" for the user's own. */
function lineKey() {
    return session.line.source === "catalog" ? session.line.slug : `line-${session.line.id}`;
}

function movesForServer() {
    return session.attempts.map((m) => ({
        ply: m.ply, moveNumber: Number(m.fenBefore.split(" ")[5]) || 1, san: m.san, uci: m.uci,
        verdict: m.verdict.code || null, winLoss: m.verdict.loss ?? null,
        bestMoveSan: m.verdict.bestSan || null, insightCode: m.insight ? m.insight.code : null,
        // Only a repertoire that reaches the position has an opinion on the move played
        inRepertoire: m.stats && m.stats.repertoire && m.stats.repertoire.covers ? m.stats.repertoire.inRepertoire : null,
    }));
}

/** Leaving the page mid-practice: keep what was played, without counting it. */
export function abandon() {
    if (!session || !isActive() || session.isRetry) return;
    const userMoves = session.moves.filter((m) => m && m.actor === "user");
    if (userMoves.length < 3) return;
    recomputeTotals();
    track.event("practice_abandoned", { line: session.line.slug || "user-line", moves: userMoves.length }, { beacon: true });
}
